import React, { useEffect, useState } from 'react';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CurrencyInput from 'react-currency-input-field';
import { useHistory, useParams } from 'react-router-dom';
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, FormControl, Link, RadioGroup, Radio, Modal, Checkbox } from '@mui/material';
import { CreditCardTypeEnum, PaymentChannel, InstaPayVService as InstapayV2Service } from '../../../services/V2/gozer';
import { checkPaymentAmount, formatMoney, handleError, isProd } from '../../Shared/Functions';
import { withToast } from '../../Shared/Toast';
import ReCAPTCHA from "react-google-recaptcha";
import { CaptchaService } from '../../../services/V2/keymaster';
import PaymentReceipt from '../../Payments/PaymentReceipt';
import { AccountInfo } from '../../Dashboard/AccountInfo';
import DOMPurify from 'dompurify';
import { InstapayFrame } from './InstapayFrame';
import { InstaPayVService, PaymentTypeEnum, TokenType } from '../../../services/V3/gozer';

const InstaPay = ({ errorToast, successToastPersist }) => {
    const { accountId, guestPay } = useParams<{ accountId: string, guestPay: string }>();
    const recaptchaRef: any = React.useRef();

    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [showConfirmOverPayment, setShowConfirmOverPayment] = useState<boolean>(false);
    const [paymentNotAllowed, setPaymentNotAllowed] = useState<boolean>(false);
    const [payment, setPayment] = useState<any>();
    const [account, setAccount] = useState<any>();
    const [terms, setTerms] = useState<any>();
    const [token, setToken] = useState<any>();
    const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
    const [canAcceptCc, setCanAcceptCc] = useState<boolean>(false);
    const [canAcceptAch, setCanAcceptAch] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [processing, setProcessing] = useState<boolean>(false);
    const [paymentMode, setPaymentMode] = useState<string | null>(null);
    const [maxExceeded, setMaxExceeded] = useState<boolean>(false);
    const [minExceeded, setMinExceeded] = useState<boolean>(false);
    const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);
    const [captchaToken, setCaptchaToken] = useState<any>();
    const [showReceipt, setShowReceipt] = useState<boolean>(false);
    const [transaction, setTransaction] = useState<any>(null);
    const [errorDetails, setErrorDetails] = useState<any>(null);
    const [paymentValue, setPaymentValue] = useState<any>("full");
    const [hasError, setHasError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>(null);
    const [zeroPayment, setZeroPayment] = useState<boolean>(false);
    const [ePaymentAllowed, setEPaymentAllowed] = useState<boolean>(true);
    const [fee, setFee] = useState<any>(0);
    const [feeError, setFeeError] = useState<boolean>(false);
    const [openPlugin, setOpenPlugin] = useState<boolean>(false);
    const [cardType, setCardType] = useState<string>("");
    const [clicked, setClicked] = useState<boolean>(false);

    useEffect(() => {
        if (!loading) return;
        InstapayV2Service.instaPayVGetBillingAccountInfo(accountId)
            .then(x => {
                const acceptCC = !x.paymentConfigs?.acceptCC,
                    acceptACH = !x.paymentConfigs?.acceptACH;

                setAccount(x)
                setPayment({
                    paymentAmount: x.amountDue
                })
                setCanAcceptCc(x.paymentConfigs?.acceptCC ?? false)
                setCanAcceptAch(x.paymentConfigs?.acceptACH ?? false)
                setPaymentNotAllowed((acceptCC ?? true) && (acceptACH ?? true))
                setEPaymentAllowed(x.paymentConfigs?.ePaymentAllowed ?? false)
                setTerms(x.termsAndConditions)
                setLoading(false)
            })
            .catch(err => err.message !== "Not Found" && errorToast(handleError(err)))
    }, [loading])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        checkPaymentAmount(payment?.paymentAmount, paymentMode,
            account?.paymentConfigs.ccMaxAmount, account?.paymentConfigs.ccMinAmount,
            account?.paymentConfigs.achMaxAmount, account?.paymentConfigs.achMinAmount,
            setHasError, setErrorMessage);
    }, [payment, account, paymentMode])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (cardType !== "") {
            presubmit();
        }
    }, [cardType])// eslint-disable-line react-hooks/exhaustive-deps

    const continueOverPayment = () => {
        setShowConfirmOverPayment(false)
        if (!hasError)
            calculateFee();
    }

    const submit = () => {
        setProcessing(true);
        InstaPayVService.instaPayVMakePayment({
            amount: payment.paymentAmount,
            tokenType: paymentMode === "CC" ? TokenType.CC : TokenType.ACH,
            tokenId: token,
            channel: guestPay.toLocaleLowerCase() === "guestpay" ? PaymentChannel.GUEST_PAY : PaymentChannel.INSTAPAY,
            billingAccountId: accountId

        })
            .then(x => {
                setErrorDetails(null);
                setIsSubmitting(false)
                setConfirmSubmit(false)
                setProcessing(false)
                setTransaction(x);
                setShowReceipt(true)
                successToastPersist(`Confirmation Number: ${x?.confirmationNumber}.\nAmount approved: ${formatMoney(x.amount)}.\nService Fee: ${formatMoney(x.serviceFeeAmount)}`)
            })
            .catch(err => {
                setErrorDetails(handleError(err));
                setTransaction({
                    amount: payment?.paymentAmount,
                    serviceFeeAmount: 0,//fee,
                    createdAt: new Date(),
                    paymentType: "CC"
                });
                setShowReceipt(true)
                err.message !== "Not Found" && errorToast(handleError(err))
                setIsSubmitting(false)
                setConfirmSubmit(false)
                setProcessing(false)
                
                setPaymentMode("")
                setOpenPlugin(false)
                setToken(null)
                setCardType("")
            })
    };


    const cancel = () => {
        setIsSubmitting(false)
        setConfirmSubmit(false)
        setCardType("")

        if (paymentMode === "CC") {
            setLoading(true)
        }
        
        setPaymentMode("")
        setOpenPlugin(false)
    }

    const presubmit = async () => {
        var captcha = captchaToken;
        if (!captchaToken) {
            await recaptchaRef?.current?.reset();
            captcha = await recaptchaRef?.current?.executeAsync();
            setCaptchaToken(captcha)
        }
        CaptchaService.captchaValidate(captcha)
            .then(x => {
                if (x.success) {
                    if (paymentMode === "ACH") {
                        if (payment.confirmAchAccount === payment.achAccount) {
                            if (payment?.paymentAmount > account.paymentConfigs.achMaxAmount) {
                                setMaxExceeded(true)
                            } else if (payment?.paymentAmount < account.paymentConfigs.achMinAmount) {
                                setMinExceeded(true)
                            } else {
                                setIsSubmitting(true)
                                if (payment?.paymentAmount > (account?.amountDue ?? 0)) {
                                    setShowConfirmOverPayment(true);
                                } else {
                                    calculateFee();
                                    // setConfirmSubmit(true)
                                }
                            }
                        } else errorToast("Account Numbers do not match")
                    } else {
                        if (payment?.paymentAmount > account.paymentConfigs.ccMaxAmount) {
                            setMaxExceeded(true)
                        } else if (payment?.paymentAmount < account.paymentConfigs.ccMinAmount) {
                            setMinExceeded(true)
                        } else if (payment?.paymentAmount <= 0) {
                            setZeroPayment(true)
                        } else {
                            setIsSubmitting(true)
                            if (payment?.paymentAmount > (account?.amountDue ?? 0)) {
                                setShowConfirmOverPayment(true);
                            } else {
                                calculateFee();
                                // setConfirmSubmit(true)
                            }
                        }
                    }
                } else {
                    errorToast("An error occurred validating the reCaptcha. Please refresh the page to try again. If errors persist, please contact support.")
                    setCaptchaToken(null)
                }
            })
    }

    const calculateFee = () => {
        let billingAccountId = account.id;
        let paymentType = paymentMode === "CC" ? PaymentTypeEnum.CC : PaymentTypeEnum.ACH;
        let ccType: CreditCardTypeEnum | undefined;

        switch (cardType.toLocaleLowerCase()) {
            case "visa": {
                ccType = CreditCardTypeEnum.VISA
                break;
            }
            case "mastercard": {
                ccType = CreditCardTypeEnum.MASTER_CARD
                break;
            }
            case "discover": {
                ccType = CreditCardTypeEnum.DISCOVER
                break;
            }
            case "amex": {
                ccType = CreditCardTypeEnum.AMERICAN_EXPRESS
                break;
            }
        }

        // setSubloading(true)
        InstaPayVService.instaPayVGetServiceFee(billingAccountId, paymentType, payment?.paymentAmount, ccType)
            .then(x => {
                setFee(x)
                setConfirmSubmit(true)
                // setSubloading(false)
            }).catch(err => {
                if (handleError(err) === "No Convenience Fees Found") {
                    setFee(0)
                    setConfirmSubmit(true)
                }
                else {
                    (err.message !== "Not Found" && err.message !== "Token not created.") && errorToast(handleError(err));
                    err.message === "Token not created." && calculateFee();
                    setFeeError(true);
                    setIsSubmitting(false);
                    // setSubloading(false);
                }

            })
    }

    const sanitizedTerms = (terms) => ({
        __html: DOMPurify.sanitize(terms),
    });

    const cancelOverPayment = () => {
        setShowConfirmOverPayment(false);
        setIsSubmitting(false);

        if (paymentMode === "CC") {
            setLoading(true)
        }
    }
    const complete = async (success) => {
        if (!success) {
            setPaymentMode("")
        } else {
            setToken(success)

            if (paymentMode === "ACH")
                await presubmit();
        }
    }

    return <>
        {/* Modals */}
        {showReceipt &&
            <PaymentReceipt
                transaction={transaction}
                setIsShowing={setShowReceipt}
                districtName={account?.districtName}
                accountNumber={account?.accountNumber}
                accountName={account?.accountName}
                errorDetails={errorDetails}
                isAnon={true} />
        }
        {showConfirmOverPayment &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Overpayment Confirmation.</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        You requested to pay {formatMoney(payment?.paymentAmount)}, but the amount due is only: {formatMoney(account?.amountDue ?? 0)}. Do you want to proceed?
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="outlined"
                            onClick={cancelOverPayment}
                            className="m-2"
                            color="primary"
                            style={{ marginRight: "10px" }}>Cancel</Button>
                        <Button variant="contained"
                            color="primary"
                            onClick={continueOverPayment}
                            className="m-2">
                            Proceed
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {minExceeded &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Min Payment amount Exceeded</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        {paymentMode === "CC" ?
                            `We’re sorry, but the minimum Credit Card payment allowed is ${formatMoney(account.paymentConfigs?.ccMinAmount)}.  Please try again.` :
                            `We’re sorry, but the minimum ACH payment allowed is ${formatMoney(account.paymentConfigs?.achMinAmount)}.  Please try again.`
                        }
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => {
                                setMinExceeded(false)
                                setLoading(true)
                            }}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {maxExceeded &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Max Payment amount Exceeded</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        {paymentMode === "CC" ?
                            `We’re sorry, but the maximum Credit Card payment allowed is ${formatMoney(account.paymentConfigs?.ccMaxAmount)}.  Please try again.` :
                            `We’re sorry, but the maximum ACH payment allowed is ${formatMoney(account.paymentConfigs?.achMaxAmount)}.  Please try again.`
                        }
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => {
                                setMaxExceeded(false)
                                setLoading(true)
                            }}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {zeroPayment &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Payment not allowed</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        Amount must be greater than zero.  Please try again.
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={() => {
                                setZeroPayment(false)
                                setLoading(true)
                            }}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {paymentNotAllowed && ePaymentAllowed &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Account Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        We’re sorry, online payments are not allowed for this account.
                        Please contact your utility office.
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => history.push("/sign-in")}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {!ePaymentAllowed &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Payment Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        This utility provider is unable to accept payments.
                        Please contact Customer Service. We apologize for any inconvenience.
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => history.push("/sign-in")}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {confirmSubmit &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Confirm Payment</DialogTitle>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={6} md={5}>
                            <b>Payment Amount</b>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            {formatMoney(Number.parseFloat(payment?.paymentAmount)?.toFixed(2))}
                        </Grid>
                        <Grid item xs={0} md={2}></Grid>
                        <Grid item xs={6} md={5}>
                            <b>Service Fee</b>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            {formatMoney(fee)}
                        </Grid>
                        <Grid item xs={0} md={2}></Grid>
                        <Grid item xs={6} md={5}>
                            <b>Total</b>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            {formatMoney((Number.parseFloat(payment?.paymentAmount) + fee))}
                        </Grid>
                    </Grid>
                    {/*Terms and Conditions */}
                    <div style={{ marginTop: "16px", textAlign: "center" }}>
                        <span>
                            <Checkbox
                                checked={clicked}
                                onChange={x => setClicked(x.target.checked)}
                                color="primary" />
                            By checking this box and completing this electronic payment, you agree to our{' '}
                            <Link
                                component="button"
                                onClick={() => setTermsAndConditions(true)}
                                className="clickable"
                                underline="always"
                                style={{ color: "#1976d2" }}
                            >
                                Terms and Conditions
                            </Link>
                        </span>
                    </div>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="outlined"
                            color="primary"
                            style={{ marginRight: "10px" }}
                            disabled={processing}
                            onClick={cancel}
                            className="m-2">Back</Button>
                        <Button variant="contained"
                            color="primary"
                            disabled={processing || feeError || !clicked}
                            onClick={submit}
                            className="m-2">Submit</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {termsAndConditions &&
            <Dialog
                fullWidth
                onClose={() => setTermsAndConditions(false)}
                open={true}>
                <DialogTitle>Terms and Conditions</DialogTitle>
                <DialogContent>
                    {(paymentMode === "CC" && terms?.ccTermsAndConditions) ||
                     (paymentMode === "ACH" && terms?.achTermsAndConditions) ? (
                        <div
                            dangerouslySetInnerHTML={paymentMode === "CC"
                                ? sanitizedTerms(terms.ccTermsAndConditions)
                                : sanitizedTerms(terms.achTermsAndConditions)}
                            style={{ fontSize: "16px" }}
                        />
                    ) : (
                        <div style={{ fontSize: "16px", color: "#b71c1c" }}>
                            Terms and Conditions are not available.
                        </div>
                    )}
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => setTermsAndConditions(false)}
                            className="m-2">Ok</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }

        {/* Main User Interface */}

        {loading ?
            <div className='loading-container'><CircularProgress color="primary" className='center-spinner' aria-label="Loading" /></div>
            :
            <>
                <Grid className='content-container'>
                    <Grid item sm={12} style={{ marginTop: "15px", marginBottom: "15px" }}>
                        <Grid style={{ display: "block", marginLeft: "auto", marginRight: "auto", maxWidth: "1200px" }}>
                            <AccountInfo account={account} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container style={{ display: "block", marginLeft: "auto", marginRight: "auto", maxWidth: "1200px", padding: "25px", backgroundColor: "White" }}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={isProd() ? "6LdysiEhAAAAAPZwsLD0CCf4U8yJvrD6pkuGuTHw" : "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                    />
                    <Grid item xs={12} className='make-payment-grid'>
                        <Grid item className='payment-info'>
                            <div><b>Payment Date</b>&nbsp;&nbsp;&nbsp;&nbsp;{new Date().toLocaleDateString()}</div>
                            <br />
                            <div><b>Payment Amount</b></div>
                            <FormControl>
                                <RadioGroup
                                    aria-label="Select payment amount"
                                    name="radio-buttons-group"
                                    value={paymentValue}
                                    onChange={(e) => {
                                        if (e.target.value === "full") {
                                            setPayment({
                                                ...payment,
                                                paymentAmount: account?.amountDue
                                            })
                                        } else if (e.target.value === "pastDue") {
                                            setPayment({
                                                ...payment,
                                                paymentAmount: account?.pastDueAmount
                                            })
                                        } else if (e.target.value === "other") {
                                            setPayment({
                                                ...payment,
                                                paymentAmount: null
                                            })
                                        }
                                        setPaymentValue(e.target.value);
                                    }}>
                                    <FormControlLabel defaultChecked value={"full"} control={<Radio />} label={`Total Amount Due ${formatMoney(account?.amountDue ?? 0)}`} />
                                    {account?.pastDueAmount > 0 &&
                                        <FormControlLabel value={"pastDue"} control={<Radio />} label={`Past Due Amount ${formatMoney(account?.pastDueAmount ?? 0)}`} />
                                    }
                                    <FormControlLabel value="other" control={<Radio />} label="Other Amount" />
                                </RadioGroup>
                                {paymentValue === "other" &&
                                    <div className="MuiFormControl-root MuiFormControl-fullWidth MuiTextField-root css-17vbkzs-MuiFormControl-root-MuiTextField-root" style={{ marginTop: "8px", marginBottom: "16px" }}>
                                        <div className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-fullWidth MuiInputBase-formControl css-decq32-MuiInputBase-root-MuiOutlinedInput-root" style={{ border: '1px solid #595959', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
                                            <CurrencyInput
                                                id="paymentAmount"
                                                className="payment-amount-input"
                                                style={{ fontSize: "1rem", padding: "6px 14px", width: "100%", boxSizing: "border-box", border: "none", outline: "none", background: "transparent" }}
                                                prefix="$"
                                                decimalScale={2}
                                                value={payment?.paymentAmount}
                                                aria-label="Other amount to pay"
                                                autoComplete="off"
                                                onValueChange={(e, m, floatValue) => setPayment({ ...payment, paymentAmount: floatValue?.value })} />
                                        </div>
                                    </div>
                                }
                                {errorMessage && errorMessage !== "" &&
                                    <div className='error'>
                                        {errorMessage}
                                    </div>
                                }
                            </FormControl>
                            {account.hasAutoPay &&
                                <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                                    <AutorenewIcon style={{ maxHeight: "15px" }} />
                                    <i>Account is enrolled in AutoPay. Your payment will be automatically processed for the amount due.</i>
                                </div>
                            }
                        </Grid>
                        <Divider className='divider' orientation='vertical' variant="middle" flexItem />
                        <Grid item className='payment-method' style={{ marginLeft: "25px" }}>
                            <div><b>Payment Method</b></div>
                            <FormControl>
                                <RadioGroup
                                    aria-label="Select payment method"
                                    name="radio-buttons-group"
                                    value={paymentMode}
                                    onChange={(e: any) => {
                                        setPaymentMode(e.target.value);
                                        setOpenPlugin(true);
                                    }}>
                                    {canAcceptCc &&
                                        <FormControlLabel value={"CC"} control={<Radio />} label={`Pay with Credit Card`} />
                                    }
                                    {canAcceptAch &&
                                        <FormControlLabel value={"ACH"} control={<Radio />} label={`Pay with Bank Account`} />
                                    }
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item className='payment-form' style={{ marginRight: "25px" }}>
                            <Modal open={openPlugin}>
                                <div style={{ width: "100%" }}>
                                    {paymentMode === "CC" && canAcceptCc ?
                                        <InstapayFrame
                                            billingAccountId={accountId}
                                            complete={complete}
                                            errorToast={errorToast}
                                            tokenType={TokenType.CC}
                                            setOpenPlugin={setOpenPlugin}
                                            setCardType={setCardType}
                                        />
                                        : paymentMode === "ACH" && canAcceptAch ?
                                            <InstapayFrame
                                                billingAccountId={accountId}
                                                complete={complete}
                                                errorToast={errorToast}
                                                tokenType={TokenType.ACH}
                                                setOpenPlugin={setOpenPlugin}
                                                setCardType={setCardType}
                                            /> : null
                                    }
                                </div>
                            </Modal>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className='make-payment-buttons-group'>
                        <Button variant="outlined"
                            style={{ width: "125px", padding: "10px", height: "fit-content" }}
                            disabled={isSubmitting}
                            color="primary"
                            onClick={() => history.push("/log-in")}> Cancel </Button>
                    </Grid>
                </Grid>
                {processing && <div className='loading-container'><CircularProgress color="primary" className='center-spinner' aria-label="Loading" /></div>}
            </>
        }
    </>
}

export default withToast(InstaPay);